import { useEffect, useState, useCallback } from "react";
import { shopProductUseCase } from "../../../di/container";
import { ShopCommonLayout } from "../../layouts/ShopCommonLayout";
import { useParams } from "react-router-dom";
import type {
  ShopProductDetail,
  ProductOptionResponse,
  Attribute,
  SampleValue,
  Option,
  CombinationRequest,
  AttributeIdRequest,
  CalculatePriceResponse,
  TriggerType,
  TriggerVisibleType,
} from "../../../domain/types/shop-product";
import { ChevronDown, Minus, Plus, ShoppingCart } from "lucide-react";

const ProductPage = () => {
  const { productId } = useParams();

  // TODO: shopId를 URL 파라미터 또는 컨텍스트에서 가져오도록 변경 필요
  // 현재는 임시로 하드코딩
  const shopId = 1;

  // 상품 정보
  const [product, setProduct] = useState<ShopProductDetail | null>(null);
  const [productOptions, setProductOptions] = useState<ProductOptionResponse | null>(null);
  const [loading, setLoading] = useState(true);

  // 옵션 선택 상태
  const [selectedAttributes, setSelectedAttributes] = useState<{
    [optionId: number]: { [attributeId: number]: AttributeIdRequest };
  }>({});
  const [optionQuantities, setOptionQuantities] = useState<{ [combinationId: number]: number }>({});
  const [productCount, setProductCount] = useState(1);

  // 가격 계산 결과
  const [calculatedPrice, setCalculatedPrice] = useState<CalculatePriceResponse | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);

  /**
   * 트리거 조건을 평가합니다.
   * @param triggerValueIds 트리거 값 ID 배열
   * @param triggerType AND/OR/NAND/NOR
   * @param selectedIds 현재 선택된 값 ID Set
   * @returns 조건 충족 여부
   */
  const evaluateTrigger = useCallback(
    (
      triggerValueIds: number[],
      triggerType: TriggerType,
      selectedIds: Set<number>
    ): boolean => {
      if (!triggerValueIds || triggerValueIds.length === 0) return true;

      const matchCount = triggerValueIds.filter((id) => selectedIds.has(id)).length;
      const allMatch = matchCount === triggerValueIds.length;
      const anyMatch = matchCount > 0;

      switch (triggerType) {
        case "AND":
          return allMatch; // 모든 트리거 값이 선택되어야 표시
        case "OR":
          return anyMatch; // 하나라도 선택되면 표시
        case "NAND":
          return !allMatch; // 모두 선택되지 않아야 표시
        case "NOR":
          return !anyMatch; // 아무것도 선택되지 않아야 표시
        default:
          return true;
      }
    },
    []
  );

  /**
   * 현재 선택된 모든 sampleValueId를 수집합니다.
   */
  const collectSelectedValueIds = useCallback((): Set<number> => {
    const selectedIds = new Set<number>();
    Object.values(selectedAttributes).forEach((optionAttrs) => {
      Object.values(optionAttrs).forEach((attr) => {
        if (attr.sampleValueId) {
          selectedIds.add(attr.sampleValueId);
        }
      });
    });
    return selectedIds;
  }, [selectedAttributes]);

  // 상품 및 옵션 데이터 로드
  useEffect(() => {
    const loadProductData = async () => {
      if (!productId) return;

      setLoading(true);
      try {
        // 상품 상세 조회
        const productDetail = await shopProductUseCase.getProductDetail(
          shopId,
          Number(productId)
        );
        setProduct(productDetail);

        // 옵션 조회
        const options = await shopProductUseCase.getProductOptions(
          shopId,
          Number(productId)
        );
        setProductOptions(options);
      } catch (error) {
        console.error("상품 정보 로드 실패:", error);
      } finally {
        setLoading(false);
      }
    };

    loadProductData();
  }, [productId]);

  // 속성 선택 핸들러
  const handleAttributeSelect = useCallback(
    (optionId: number, attributeId: number, sampleValueId: number, value: string) => {
      setSelectedAttributes((prev) => ({
        ...prev,
        [optionId]: {
          ...prev[optionId],
          [attributeId]: {
            attributeId,
            sampleValueId,
            value,
          },
        },
      }));
    },
    []
  );

  // 텍스트 입력 핸들러
  const handleTextAttributeChange = useCallback(
    (optionId: number, attributeId: number, value: string) => {
      setSelectedAttributes((prev) => ({
        ...prev,
        [optionId]: {
          ...prev[optionId],
          [attributeId]: {
            attributeId,
            value,
          },
        },
      }));
    },
    []
  );

  // 수량 변경 핸들러
  const handleQuantityChange = useCallback((delta: number) => {
    setProductCount((prev) => Math.max(1, prev + delta));
  }, []);

  // 가격 계산
  const handleCalculatePrice = useCallback(async () => {
    if (!product || !productOptions) return;

    setIsCalculating(true);
    try {
      const combinations: CombinationRequest[] = [];

      // 각 옵션에 대해 조합 생성
      for (const option of productOptions.options) {
        const selectedAttrs = selectedAttributes[option.id];
        if (!selectedAttrs) continue;

        const attributeIds = Object.values(selectedAttrs);
        if (attributeIds.length === 0) continue;

        // 해당 조합 ID 찾기
        const combination = productOptions.combinations.find((c) =>
          c.attributeIds.some((aid) =>
            attributeIds.some((attr) => attr.attributeId === aid)
          )
        );

        if (combination) {
          combinations.push({
            combinationId: combination.id,
            attributeIds,
            selectableCount: optionQuantities[combination.id] || 1,
          });
        }
      }

      const result = await shopProductUseCase.calculatePrice(
        shopId,
        product.id,
        {
          productId: product.id,
          productCount,
          combinations,
        }
      );
      setCalculatedPrice(result);
    } catch (error) {
      console.error("가격 계산 실패:", error);
    } finally {
      setIsCalculating(false);
    }
  }, [product, productOptions, selectedAttributes, optionQuantities, productCount]);

  // 기본 가격 계산 (priceTiers 기반)
  const getBasePrice = useCallback(() => {
    if (!product || !product.priceTiers || product.priceTiers.length === 0) {
      return 0;
    }

    // 수량에 맞는 가격 구간 찾기
    const tier = product.priceTiers
      .sort((a, b) => b.minQuantity - a.minQuantity)
      .find((t) => productCount >= t.minQuantity);

    return tier ? tier.price : product.priceTiers[0].price;
  }, [product, productCount]);

  // 속성 렌더링
  const renderAttribute = (option: Option, attribute: Attribute, depth = 0) => {
    const selectedValue = selectedAttributes[option.id]?.[attribute.id];
    const selectedIds = collectSelectedValueIds();

    // 트리거 조건 평가
    let shouldShow = true;
    let isDisabled = false;
    const triggerVisibleType = attribute.triggerVisibleType || "HIDE";

    // 새로운 트리거 로직 (triggerAttributeValueIds 기반)
    if (attribute.triggerAttributeValueIds && attribute.triggerAttributeValueIds.length > 0) {
      const triggerType = attribute.triggerType || "OR";
      shouldShow = evaluateTrigger(attribute.triggerAttributeValueIds, triggerType, selectedIds);
    }
    // 기존 parentValueId 로직 (하위 호환성)
    else if (attribute.parentValueId !== null) {
      const parentSelected = Object.values(selectedAttributes[option.id] || {}).some(
        (attr) => attr.sampleValueId === attribute.parentValueId
      );
      shouldShow = parentSelected;
    }

    // HIDE vs DISABLE 처리
    if (!shouldShow) {
      if (triggerVisibleType === "DISABLE") {
        isDisabled = true;
      } else {
        // HIDE: 완전히 숨김
        return null;
      }
    }

    const renderSampleValue = (sv: SampleValue) => {
      const isSelected = selectedValue?.sampleValueId === sv.id;
      const disabledClass = isDisabled ? "opacity-50 cursor-not-allowed grayscale" : "";

      if (attribute.previewType === "SELECT_BOX_WITH_IMAGE") {
        return (
          <button
            key={sv.id}
            onClick={() => !isDisabled && handleAttributeSelect(option.id, attribute.id, sv.id, sv.value)}
            disabled={isDisabled}
            className={`flex flex-col items-center p-2 border rounded-lg transition-all ${
              isSelected
                ? "border-blue-500 bg-blue-50 ring-2 ring-blue-200"
                : "border-gray-200 hover:border-gray-300"
            } ${disabledClass}`}
          >
            {sv.imageUrl && (
              <img
                src={sv.imageUrl}
                alt={sv.value}
                className="w-16 h-16 object-cover rounded mb-1"
              />
            )}
            <span className="text-sm text-center">{sv.value}</span>
            {sv.description && (
              <span className="text-xs text-gray-500">{sv.description}</span>
            )}
          </button>
        );
      }

      // 기본 SELECT_BOX
      return (
        <button
          key={sv.id}
          onClick={() => !isDisabled && handleAttributeSelect(option.id, attribute.id, sv.id, sv.value)}
          disabled={isDisabled}
          className={`px-4 py-2 border rounded-lg text-sm transition-all ${
            isSelected
              ? "border-blue-500 bg-blue-500 text-white"
              : "border-gray-200 hover:border-gray-300"
          } ${disabledClass}`}
        >
          {sv.value}
          {sv.width && sv.height && (
            <span className="ml-1 text-xs opacity-75">
              ({sv.width}x{sv.height})
            </span>
          )}
        </button>
      );
    };

    const disabledContainerClass = isDisabled ? "opacity-50" : "";

    return (
      <div key={attribute.id} className={`${depth > 0 ? "ml-4 mt-3" : ""} ${disabledContainerClass}`}>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {attribute.name}
          {attribute.isRequiredAttribute && (
            <span className="text-red-500 ml-1">*</span>
          )}
          {isDisabled && (
            <span className="text-gray-400 ml-2 text-xs">(비활성화됨)</span>
          )}
        </label>

        {attribute.previewType === "INPUT_BOX" ? (
          <input
            type={attribute.attributeType === "NUMBER" ? "number" : "text"}
            value={selectedValue?.value || ""}
            onChange={(e) =>
              !isDisabled && handleTextAttributeChange(option.id, attribute.id, e.target.value)
            }
            disabled={isDisabled}
            placeholder={attribute.description || `${attribute.name} 입력`}
            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isDisabled ? "cursor-not-allowed bg-gray-100" : ""}`}
          />
        ) : attribute.previewType === "DROP_DOWN_MENU" ? (
          <div className="relative">
            <select
              value={selectedValue?.sampleValueId || ""}
              onChange={(e) => {
                if (isDisabled) return;
                const sv = attribute.sampleValues?.find(
                  (s) => s.id === Number(e.target.value)
                );
                if (sv) {
                  handleAttributeSelect(option.id, attribute.id, sv.id, sv.value);
                }
              }}
              disabled={isDisabled}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none ${isDisabled ? "cursor-not-allowed bg-gray-100" : ""}`}
            >
              <option value="">선택하세요</option>
              {attribute.sampleValues?.map((sv) => (
                <option key={sv.id} value={sv.id}>
                  {sv.value}
                  {sv.width && sv.height && ` (${sv.width}x${sv.height})`}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {attribute.sampleValues?.map(renderSampleValue)}
          </div>
        )}

        {/* 자식 속성 렌더링 */}
        {attribute.childAttributes?.map((child) =>
          renderAttribute(option, child, depth + 1)
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <ShopCommonLayout>
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
        </div>
      </ShopCommonLayout>
    );
  }

  if (!product) {
    return (
      <ShopCommonLayout>
        <div className="flex justify-center items-center min-h-[400px]">
          <p className="text-gray-500">상품을 찾을 수 없습니다.</p>
        </div>
      </ShopCommonLayout>
    );
  }

  const basePrice = getBasePrice();

  return (
    <ShopCommonLayout>
      <div className="py-8 px-4">
        <div className="max-w-[1200px] mx-auto">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* 좌측: 상품 이미지 */}
            <div className="lg:w-1/2">
              <div className="sticky top-4">
                <img
                  src={product.thumbnail ?? "/placeholder-image.png"}
                  alt={product.name}
                  className="w-full aspect-square object-cover rounded-lg border border-gray-200"
                />
              </div>
            </div>

            {/* 우측: 상품 정보 및 옵션 */}
            <div className="lg:w-1/2">
              {/* 상품명 및 설명 */}
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {product.name}
              </h1>
              {product.description && (
                <p className="text-gray-600 mb-4">{product.description}</p>
              )}

              {/* 기본 가격 */}
              <div className="text-xl font-semibold text-blue-600 mb-6">
                ₩{basePrice.toLocaleString()}
                <span className="text-sm text-gray-500 font-normal ml-2">
                  (기본가)
                </span>
              </div>

              {/* 수량 선택 */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  수량
                </label>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleQuantityChange(-1)}
                    disabled={productCount <= 1}
                    className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-16 text-center text-lg font-medium">
                    {productCount}
                  </span>
                  <button
                    onClick={() => handleQuantityChange(1)}
                    className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* 옵션 선택 영역 */}
              {productOptions && productOptions.options.length > 0 && (
                <div className="space-y-6 mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                    옵션 선택
                  </h3>
                  {productOptions.options
                    .sort((a, b) => a.displayOrder - b.displayOrder)
                    .map((option) => (
                      <div
                        key={option.id}
                        className="bg-gray-50 p-4 rounded-lg"
                      >
                        <h4 className="font-medium text-gray-900 mb-3">
                          {option.name}
                          {option.isRequiredOption && (
                            <span className="text-red-500 ml-1">*</span>
                          )}
                        </h4>
                        {option.description && (
                          <p className="text-sm text-gray-500 mb-3">
                            {option.description}
                          </p>
                        )}
                        <div className="space-y-4">
                          {option.attributes
                            .filter((attr) => attr.parentValueId === null)
                            .sort((a, b) => a.displayOrder - b.displayOrder)
                            .map((attr) => renderAttribute(option, attr))}
                        </div>
                      </div>
                    ))}

                  {/* 가격 계산 버튼 */}
                  <button
                    onClick={handleCalculatePrice}
                    disabled={isCalculating}
                    className="w-full py-3 bg-gray-800 text-white rounded-lg font-medium hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isCalculating ? "계산 중..." : "견적 계산하기"}
                  </button>
                </div>
              )}

              {/* 견적식 미리보기 */}
              {calculatedPrice && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    견적식 미리보기
                  </h3>
                  <div className="space-y-3">
                    {/* 상품 가격 */}
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">
                        {calculatedPrice.productName} × {calculatedPrice.productQuantity}
                      </span>
                      <span className="font-medium">
                        ₩{calculatedPrice.productPrice.toLocaleString()}
                      </span>
                    </div>

                    {/* 옵션별 가격 */}
                    {calculatedPrice.optionPrices.length > 0 && (
                      <>
                        <div className="border-t border-blue-200 pt-3">
                          <span className="text-sm font-medium text-gray-600">
                            옵션 가격
                          </span>
                        </div>
                        {calculatedPrice.optionPrices.map((opt, idx) => (
                          <div
                            key={idx}
                            className="flex justify-between items-center pl-3"
                          >
                            <span className="text-gray-600 text-sm">
                              {opt.optionName}
                              {opt.grammage && (
                                <span className="text-gray-500 ml-1">
                                  {opt.grammage}g
                                </span>
                              )}
                              {opt.size && (
                                <span className="text-gray-400 ml-1">
                                  ({opt.size})
                                </span>
                              )}
                              {opt.optionQuantity > 1 && (
                                <span className="text-gray-400 ml-1">
                                  × {opt.optionQuantity}
                                </span>
                              )}
                            </span>
                            <span className="text-gray-700">
                              ₩{opt.optionPrice.toLocaleString()}
                            </span>
                          </div>
                        ))}
                      </>
                    )}

                    {/* 소계 */}
                    <div className="flex justify-between items-center border-t border-blue-200 pt-3">
                      <span className="text-gray-700">소계</span>
                      <span className="font-medium">
                        ₩{calculatedPrice.calculateAmount.toLocaleString()}
                      </span>
                    </div>

                    {/* 부가세 */}
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">부가세 (VAT)</span>
                      <span className="font-medium">
                        ₩{calculatedPrice.vatAmount.toLocaleString()}
                      </span>
                    </div>

                    {/* 최종 금액 */}
                    <div className="flex justify-between items-center border-t-2 border-blue-300 pt-3 mt-3">
                      <span className="text-lg font-bold text-gray-900">
                        최종 금액
                      </span>
                      <span className="text-xl font-bold text-blue-600">
                        ₩{calculatedPrice.totalAmount.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* 장바구니/주문 버튼 */}
              <div className="flex gap-3">
                <button className="flex-1 py-3 bg-white border-2 border-blue-500 text-blue-500 rounded-lg font-medium hover:bg-blue-50 transition-colors flex items-center justify-center gap-2">
                  <ShoppingCart className="w-5 h-5" />
                  장바구니
                </button>
                <button className="flex-1 py-3 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors">
                  주문하기
                </button>
              </div>
            </div>
          </div>

          {/* 상품 상세정보 이미지 (860px 기준) */}
          {product.detailPage && product.detailPage.length > 0 && (
            <section className="mt-12">
              <div className="border-t border-gray-200 mb-12" />
              <div className="max-w-[860px] mx-auto flex flex-col">
                {[...product.detailPage]
                  .sort((a, b) => a.displayOrder - b.displayOrder)
                  .map((img) => (
                    <img
                      key={img.id}
                      src={img.imageUrl}
                      alt={`상세 이미지 ${img.displayOrder}`}
                      className="block w-full max-w-[860px] h-auto"
                    />
                  ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </ShopCommonLayout>
  );
};

export default ProductPage;
